import { API } from "@swivl-mono/api-helper";
import { PhoneNumberHelper, filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonSize, ButtonVariant, Modal, PhoneInput, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { CustomerPickerModal, SelectedCustomer } from "../../../Components/CustomerPickerModal/CustomerPickerModal";
import ContactCenterV3Model from "../../../Models/ContactCenterV3/ContactCenterV3.Store";
import { Member } from "../../../Models/Member/Member.Current";
import { OutboundNumberSelection, SelectOutboundNumberModal } from "./SelectOutboundNumberModal";

type CallToMode = "customer" | "direct";

interface NewOutboundCallModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "New Outbound Call" (ENG-3018, Figma 903-18929 / 1273-19427): call a
 * directory customer or direct-dial any number. Call From defaults to the
 * server-resolved "Preferred" number (customer location → agent default →
 * org primary); picking one explicitly overrides it for this call only.
 * Direct dial to a known number takes the same path as picking that customer;
 * unknown numbers get a user created before the call (bot-side), and either
 * way the agent lands on that customer's conversation.
 */
export function NewOutboundCallModal({ open, onClose }: NewOutboundCallModalProps): React.ReactNode {
  const navigate = useNavigate();
  const [mode, setMode] = useState<CallToMode>("customer");
  const [customer, setCustomer] = useState<SelectedCustomer | null>(null);
  const [directNumber, setDirectNumber] = useState("");
  const [preferred, setPreferred] = useState<{ identifier: string | null; name: string | null } | null>(null);
  const [explicitFrom, setExplicitFrom] = useState<OutboundNumberSelection | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [numberPickerOpen, setNumberPickerOpen] = useState(false);
  const [placing, setPlacing] = useState(false);

  // The "Preferred" number is what the live call path would resolve to for
  // this target — recomputed when the selected customer changes (their
  // location can drive it).
  const customerId = mode === "customer" ? customer?.id : undefined;
  useEffect(() => {
    if (!open || !Member.orgId || !Member.id) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const result = await API.ContactCenterV3.getPreferredFromNumber(Member.orgId!, Member.id!, customerId);
        if (!cancelled) setPreferred(result);
      } catch {
        if (!cancelled) setPreferred(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, customerId]);

  if (!open) return null;

  const toNumber = mode === "customer" ? (customer?.phone ?? "") : directNumber;
  const directNumberValid = !!PhoneNumberHelper.isPhoneNumberValid(directNumber);
  const directNumberError = mode === "direct" && directNumber.trim() !== "" && !directNumberValid ? "Enter a valid phone number." : undefined;
  const canPlace = !placing && (mode === "customer" ? !!customer?.phone : directNumberValid);
  const fromDisplay =
    explicitFrom ?? (preferred?.identifier ? { identifier: preferred.identifier, name: preferred.name ?? preferred.identifier } : null);

  const handlePlaceCall = async (): Promise<void> => {
    if (!canPlace) return;
    setPlacing(true);
    const resultUserId = await ContactCenterV3Model.initiateOutboundCall(
      toNumber,
      mode === "customer" ? customer?.id : undefined,
      explicitFrom?.identifier
    );
    setPlacing(false);
    if (resultUserId !== null) {
      onClose();
      // Land on the Voice tab — the call's conversation lives there; detail
      // loading only picks a default channel when none is selected.
      ContactCenterV3Model.setState({ selectedChannel: "voice" });
      navigate(`/contact-center-v3/conversations/${resultUserId}`);
    }
  };

  const modeCard = (cardMode: CallToMode, title: string, subtitle: string): React.ReactElement => {
    const active = mode === cardMode;
    return (
      <button
        type="button"
        onClick={() => setMode(cardMode)}
        className={filterClasses(
          "flex flex-1 cursor-pointer flex-col gap-1 rounded-md border p-3 text-left transition-colors",
          active ? "border-orange-500 bg-orange-50" : "border-grey-200 bg-white hover:border-grey-300"
        )}
      >
        <span className="flex items-center justify-between">
          <span className={filterClasses("body-sm-alt", active ? "text-black" : "text-grey-800")}>{title}</span>
          <span
            className={filterClasses(
              "inline-block size-4 rounded-full border-2",
              active ? "border-orange-500 bg-orange-500 shadow-[inset_0_0_0_2.5px_white]" : "border-grey-300 bg-white"
            )}
          />
        </span>
        <span className="body-sm text-grey-500">{subtitle}</span>
      </button>
    );
  };

  return (
    <>
      <Modal isOpen={open} onClose={(isOpen) => !isOpen && onClose()} title="New Outbound Call" className="w-[480px]" hasTitleBorder>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-2">
            <span className="body-sm-alt text-grey-700">Call To</span>
            <div className="flex gap-3">
              {modeCard("customer", "Customer", "Search and select from the customer directory")}
              {modeCard("direct", "Direct Dial", "Enter any phone number to dial directly")}
            </div>
            {mode === "customer" ? (
              <div className="flex items-center justify-between rounded-md border border-grey-200 px-3 py-2.5">
                <span className="flex flex-col">
                  <span className="body-sm-alt text-grey-800">{customer?.name ?? "Customer"}</span>
                  {customer?.phone && <span className="body-sm text-grey-500">{customer.phone}</span>}
                </span>
                <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setCustomerPickerOpen(true)}>
                  Select <FiExternalLink size={12} />
                </Button>
              </div>
            ) : (
              <PhoneInput placeholder="555-000-000" onChange={setDirectNumber} error={directNumberError} />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="body-sm-alt text-grey-700">Call From</span>
            <div className="flex items-center justify-between rounded-md border border-grey-200 px-3 py-2.5">
              <span className="flex flex-col">
                <span className="body-sm-alt text-grey-800">{fromDisplay?.name ?? "Outbound Number"}</span>
                {fromDisplay && (
                  <span className="flex items-center gap-1.5">
                    <span className="body-sm text-grey-500">{fromDisplay.identifier}</span>
                    {!explicitFrom && <span className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">Preferred</span>}
                  </span>
                )}
              </span>
              <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => setNumberPickerOpen(true)}>
                Select <FiExternalLink size={12} />
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button variant={ButtonVariant.GHOST} onClick={onClose}>
              Cancel
            </Button>
            <Button variant={ButtonVariant.PRIMARY} disabled={!canPlace} onClick={() => void handlePlaceCall()}>
              {placing && <Spinner />} Place Call
            </Button>
          </div>
        </div>
      </Modal>

      <CustomerPickerModal
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onSelect={(selected) => {
          setCustomer(selected);
          setCustomerPickerOpen(false);
        }}
        selectedCustomer={customer}
        onRemove={() => setCustomer(null)}
      />

      <SelectOutboundNumberModal
        open={numberPickerOpen}
        onClose={() => setNumberPickerOpen(false)}
        onSelect={setExplicitFrom}
        selectedIdentifier={explicitFrom?.identifier ?? preferred?.identifier}
      />
    </>
  );
}
